import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '../includes/sidebar';
import { Topbar } from '../includes/topbar';
import {
    Breadcrumb, BreadcrumbItem, BreadcrumbLink,
    BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog, DialogContent, DialogDescription,
    DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    Settings, RefreshCw, Loader2, Edit2, AlertCircle,
    DollarSign, Layers, Wallet, Server, Info, Check, ShieldCheck, ShieldAlert
} from 'lucide-react';
import {
    cardAdminApi,
    CardSystemConfig, CardFeeConfig, CardTierConfig, CardAccount,
} from '../../api/cards.api';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';

type Tab = 'system' | 'fees' | 'tiers' | 'account';

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'system', label: 'System Config', icon: <Server className="h-4 w-4" /> },
    { key: 'fees',   label: 'Frè & Limit',   icon: <DollarSign className="h-4 w-4" /> },
    { key: 'tiers',  label: 'Tiers',          icon: <Layers className="h-4 w-4" /> },
    { key: 'account',label: 'Kont Marchan',   icon: <Wallet className="h-4 w-4" /> },
];

const formatUSD = (v?: number | string) =>
    v != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(v)) : '—';

const formatPercent = (v?: number | string) =>
    v != null ? `${Number(v).toFixed(2)}%` : '—';

const formatDate = (d?: string | null) =>
    d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export default function CardConfigPage() {
    const { toast } = useToast();
    const [activeTab, setActiveTab] = useState<Tab>('system');

    // System Config States
    const [configs, setConfigs] = useState<CardSystemConfig[]>([]);
    const [loadingConfigs, setLoadingConfigs] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState<CardSystemConfig | null>(null);
    const [tempConfigValue, setTempConfigValue] = useState('');
    const [savingConfig, setSavingConfig] = useState(false);

    // Fee Config States
    const [feeConfigs, setFeeConfigs] = useState<CardFeeConfig[]>([]);
    const [loadingFees, setLoadingFees] = useState(false);
    const [selectedFee, setSelectedFee] = useState<CardFeeConfig | null>(null);
    const [tempFeeFields, setTempFeeFields] = useState<Partial<CardFeeConfig>>({});
    const [savingFee, setSavingFee] = useState(false);

    // Tier Config States
    const [tiers, setTiers] = useState<CardTierConfig[]>([]);
    const [loadingTiers, setLoadingTiers] = useState(false);
    const [selectedTier, setSelectedTier] = useState<CardTierConfig | null>(null);
    const [tempTierFields, setTempTierFields] = useState<Partial<CardTierConfig>>({});
    const [savingTier, setSavingTier] = useState(false);

    // Merchant Account States
    const [account, setAccount] = useState<CardAccount | null>(null);
    const [loadingAccount, setLoadingAccount] = useState(false);

    // ─── Data Loaders ──────────────────────────────────────────────────────────

    const loadSystemConfigs = useCallback(async () => {
        try {
            setLoadingConfigs(true);
            const res = await cardAdminApi.getSystemConfigs();
            setConfigs(res);
        } catch {
            toast({ title: 'Erè', description: 'Pa kapab chaje konfigirasyon sistèm yo', type: 'error' });
        } finally {
            setLoadingConfigs(false);
        }
    }, [toast]);

    const loadFeeConfigs = useCallback(async () => {
        try {
            setLoadingFees(true);
            const res = await cardAdminApi.getFeeConfigs();
            setFeeConfigs(res);
        } catch {
            toast({ title: 'Erè', description: 'Pa kapab chaje konfigirasyon frè yo', type: 'error' });
        } finally {
            setLoadingFees(false);
        }
    }, [toast]);

    const loadCardTiers = useCallback(async () => {
        try {
            setLoadingTiers(true);
            const res = await cardAdminApi.getCardTiers();
            setTiers(res);
        } catch {
            toast({ title: 'Erè', description: 'Pa kapab chaje nivo kat yo (tiers)', type: 'error' });
        } finally {
            setLoadingTiers(false);
        }
    }, [toast]);

    const loadCardAccount = useCallback(async () => {
        try {
            setLoadingAccount(true);
            const res = await cardAdminApi.getCardAccount();
            setAccount(res);
        } catch {
            toast({ title: 'Erè', description: 'Pa kapab chaje kont marchan an', type: 'error' });
        } finally {
            setLoadingAccount(false);
        }
    }, [toast]);

    useEffect(() => {
        if (activeTab === 'system') loadSystemConfigs();
        else if (activeTab === 'fees') loadFeeConfigs();
        else if (activeTab === 'tiers') loadCardTiers();
        else if (activeTab === 'account') loadCardAccount();
    }, [activeTab, loadSystemConfigs, loadFeeConfigs, loadCardTiers, loadCardAccount]);

    // ─── Actions ──────────────────────────────────────────────────────────────

    const handleUpdateSystemConfig = async () => {
        if (!selectedConfig) return;
        try {
            setSavingConfig(true);
            await cardAdminApi.updateSystemConfig(selectedConfig.key, tempConfigValue);
            
            // Auto update fx_rate_updated_at if editing fx_rate_* keys
            if (selectedConfig.key === 'fx_rate_htg_usdt' || selectedConfig.key === 'fx_rate_usd_usdt') {
                await cardAdminApi.updateSystemConfig('fx_rate_updated_at', new Date().toISOString());
            }

            toast({ title: 'Siksè ✓', description: 'Konfigirasyon sistèm nan mete ajou avèk siksè', type: 'success' });
            setSelectedConfig(null);
            loadSystemConfigs();
        } catch (err: any) {
            toast({
                title: 'Erè',
                description: err.response?.data?.error || 'Yon erè rive pandan modifikasyon an',
                type: 'error',
            });
        } finally {
            setSavingConfig(false);
        }
    };

    const handleUpdateFeeConfig = async () => {
        if (!selectedFee) return;
        try {
            setSavingFee(true);
            const dto = {
                applyFeeUsdt: Number(tempFeeFields.applyFeeUsdt),
                hyperpayApplyFeeUsdt: Number(tempFeeFields.hyperpayApplyFeeUsdt),
                bousanmApplyFeeUsdt: Number(tempFeeFields.bousanmApplyFeeUsdt),
                monthlyFeeUsdt: Number(tempFeeFields.monthlyFeeUsdt),
                rechargeFeePercent: Number(tempFeeFields.rechargeFeePercent),
                rechargeFeeFixedUsdt: Number(tempFeeFields.rechargeFeeFixedUsdt),
                hyperPayFeePercent: Number(tempFeeFields.hyperPayFeePercent),
                bousanmFeePercent: Number(tempFeeFields.bousanmFeePercent),
                txFeePercent: Number(tempFeeFields.txFeePercent),
                txFeeFixedUsdt: Number(tempFeeFields.txFeeFixedUsdt),
                minRechargeUsdt: Number(tempFeeFields.minRechargeUsdt),
                maxRechargeUsdt: Number(tempFeeFields.maxRechargeUsdt),
                dailySpendLimitUsdt: Number(tempFeeFields.dailySpendLimitUsdt),
                monthlySpendLimitUsdt: Number(tempFeeFields.monthlySpendLimitUsdt),
                referralCommissionPercent: Number(tempFeeFields.referralCommissionPercent),
                isActive: Boolean(tempFeeFields.isActive),
            };

            await cardAdminApi.updateFeeConfig(selectedFee.id, dto);
            toast({ title: 'Siksè ✓', description: 'Konfigirasyon frè ak limit sa yo mete ajou avèk siksè', type: 'success' });
            setSelectedFee(null);
            loadFeeConfigs();
        } catch (err: any) {
            toast({
                title: 'Erè',
                description: err.response?.data?.message || err.response?.data?.error || 'Yon erè rive pandan modifikasyon an',
                type: 'error',
            });
        } finally {
            setSavingFee(false);
        }
    };

    const handleUpdateCardTier = async () => {
        if (!selectedTier) return;
        try {
            setSavingTier(true);
            const dto: Partial<CardTierConfig> = {
                name: tempTierFields.name,
                description: tempTierFields.description,
                badgeColor: tempTierFields.badgeColor,
                maxCardsPerClient: tempTierFields.maxCardsPerClient != null ? Number(tempTierFields.maxCardsPerClient) : undefined,
                sortOrder: tempTierFields.sortOrder != null ? Number(tempTierFields.sortOrder) : undefined,
                isActive: tempTierFields.isActive != null ? Boolean(tempTierFields.isActive) : undefined,
            };

            await cardAdminApi.updateCardTier(selectedTier.id, dto);
            toast({ title: 'Siksè ✓', description: 'Nivo kat (tier) sa a mete ajou avèk siksè', type: 'success' });
            setSelectedTier(null);
            loadCardTiers();
        } catch (err: any) {
            toast({
                title: 'Erè',
                description: err.response?.data?.message || err.response?.data?.error || 'Yon erè rive pandan modifikasyon an',
                type: 'error',
            });
        } finally {
            setSavingTier(false);
        }
    };

    // Sort function for configs: epingle fx_rate_* an premye, puis trie par key asc
    const getSortedConfigs = () => {
        return [...configs].sort((a, b) => {
            const isFXA = a.key.startsWith('fx_rate_');
            const isFXB = b.key.startsWith('fx_rate_');
            if (isFXA && !isFXB) return -1;
            if (!isFXA && isFXB) return 1;
            return a.key.localeCompare(b.key);
        });
    };

    return (
        <div className="flex h-screen overflow-hidden bg-slate-50/50">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">
                    
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem><BreadcrumbLink>Kat VISA</BreadcrumbLink></BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem><BreadcrumbPage>Paramèt</BreadcrumbPage></BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Paramèt Kat VISA</h1>
                            <p className="text-slate-500 text-sm mt-1">
                                Jere konfigirasyon jeneral, frè, limit ak kont operasyon marchan an.
                            </p>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="gap-2 border-slate-200 bg-white hover:bg-slate-50 self-start sm:self-auto"
                            onClick={() => {
                                if (activeTab === 'system') loadSystemConfigs();
                                else if (activeTab === 'fees') loadFeeConfigs();
                                else if (activeTab === 'tiers') loadCardTiers();
                                else if (activeTab === 'account') loadCardAccount();
                            }}
                        >
                            <RefreshCw className="h-4 w-4" /> Aktyalize
                        </Button>
                    </div>

                    {/* Navigation Tabs */}
                    <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 overflow-x-auto shadow-sm">
                        {TABS.map(({ key, label, icon }) => (
                            <button
                                key={key}
                                onClick={() => setActiveTab(key)}
                                className={cn(
                                    'flex items-center gap-2 flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                                    activeTab === key ? 'bg-primary-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50'
                                )}
                            >
                                {icon}
                                {label}
                            </button>
                        ))}
                    </div>

                    {/* ─── TAB CONTENT: System Config ────────────────────────────────────────── */}
                    {activeTab === 'system' && (
                        <Card className="border-slate-200 shadow-sm overflow-hidden bg-white">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50/50 border-b border-slate-200">
                                            <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Konfigirasyon</th>
                                            <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Deskripsyon</th>
                                            <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Valè</th>
                                            <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Aksyon</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {loadingConfigs ? (
                                            <tr>
                                                <td colSpan={4} className="px-6 py-16 text-center">
                                                    <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto" />
                                                    <p className="text-slate-500 mt-3 text-sm">Ap chaje konfigirasyon yo...</p>
                                                </td>
                                            </tr>
                                        ) : configs.length === 0 ? (
                                            <tr>
                                                <td colSpan={4} className="px-6 py-16 text-center text-slate-400 font-medium">
                                                    <Server className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                                                    Okenn konfigirasyon jwenn
                                                </td>
                                            </tr>
                                        ) : (
                                            getSortedConfigs().map((cfg) => (
                                                <tr key={cfg.id} className="hover:bg-slate-50/50 transition-colors">
                                                    <td className="px-6 py-4 font-mono text-sm font-semibold text-slate-900">
                                                        {cfg.key}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500 max-w-xs sm:max-w-md break-words">
                                                        {cfg.description || '—'}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        {cfg.valueType === 'boolean' ? (
                                                            <Badge variant="outline" className={cn(
                                                                'font-semibold text-xs border shadow-sm',
                                                                cfg.value === 'true'
                                                                    ? 'bg-green-50 text-green-700 border-green-200'
                                                                    : 'bg-red-50 text-red-700 border-red-200'
                                                            )}>
                                                                {cfg.value === 'true' ? 'AKTIVE' : 'DEZAKTIVE'}
                                                            </Badge>
                                                        ) : cfg.key.startsWith('fx_rate_') && cfg.key !== 'fx_rate_updated_at' ? (
                                                            <span className="font-bold text-slate-800 font-mono">
                                                                {cfg.value}
                                                            </span>
                                                        ) : cfg.key === 'fx_rate_updated_at' ? (
                                                            <span className="text-xs text-slate-400 font-mono">
                                                                {formatDate(cfg.value)}
                                                            </span>
                                                        ) : (
                                                            <span className="text-sm font-medium text-slate-700">
                                                                {cfg.value}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        {cfg.key !== 'fx_rate_updated_at' && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="hover:bg-slate-100 h-8 gap-1.5 text-primary-600"
                                                                onClick={() => {
                                                                    setSelectedConfig(cfg);
                                                                    setTempConfigValue(cfg.value);
                                                                }}
                                                            >
                                                                <Edit2 className="h-3.5 w-3.5" /> Edite
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    )}

                    {/* ─── TAB CONTENT: Frè & Limit ───────────────────────────────────────────── */}
                    {activeTab === 'fees' && (
                        <div className="space-y-6">
                            {loadingFees ? (
                                <div className="py-24 text-center">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto" />
                                    <p className="text-slate-500 mt-3 text-sm">Ap chaje frè ak limit yo...</p>
                                </div>
                            ) : feeConfigs.length === 0 ? (
                                <Card className="py-16 text-center text-slate-400 font-medium">
                                    <DollarSign className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                                    Okenn konfigirasyon frè jwenn
                                </Card>
                            ) : (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {feeConfigs.map((fee) => (
                                        <Card key={fee.id} className="border-slate-200 shadow-sm overflow-hidden bg-white">
                                            <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between py-4">
                                                <div>
                                                    <CardTitle className="text-lg font-bold text-slate-900 capitalize">
                                                        Tier {fee.cardTier}
                                                    </CardTitle>
                                                    <CardDescription className="text-xs">
                                                        Frè ak limit ki aplike sou nivo sa
                                                    </CardDescription>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="border-slate-200 text-primary-600 gap-1.5 h-9"
                                                    onClick={() => {
                                                        setSelectedFee(fee);
                                                        setTempFeeFields(fee);
                                                    }}
                                                >
                                                    <Edit2 className="h-3.5 w-3.5" /> Modifye
                                                </Button>
                                            </CardHeader>
                                            <CardContent className="pt-6">
                                                <div className="space-y-6">
                                                    <div>
                                                        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Frè Aplikasyon</h4>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <ValueRow label="Frè aplikasyon total" value={formatUSD(fee.applyFeeUsdt)} />
                                                            <ValueRow label="Frè mansyèl" value={formatUSD(fee.monthlyFeeUsdt)} />
                                                            <ValueRow label="└→ Pati HyperPay" value={formatUSD(fee.hyperpayApplyFeeUsdt)} indent />
                                                            <ValueRow label="└→ Pati Bousanm" value={formatUSD(fee.bousanmApplyFeeUsdt)} indent />
                                                        </div>
                                                    </div>

                                                    <div className="border-t border-slate-100 pt-4">
                                                        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Frè Rechaj</h4>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <ValueRow label="Frè rechaj TOTAL %" value={formatPercent(fee.rechargeFeePercent)} highlight />
                                                            <ValueRow label="Frè rechaj fiks" value={formatUSD(fee.rechargeFeeFixedUsdt)} />
                                                            <ValueRow label="└→ Pati HyperPay %" value={formatPercent(fee.hyperPayFeePercent)} indent />
                                                            <ValueRow label="└→ Pati Bousanm %" value={formatPercent(fee.bousanmFeePercent)} indent />
                                                        </div>
                                                    </div>

                                                    <div className="border-t border-slate-100 pt-4">
                                                        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Frè Transaksyon</h4>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <ValueRow label="Frè transaksyon %" value={formatPercent(fee.txFeePercent)} />
                                                            <ValueRow label="Frè transaksyon fiks" value={formatUSD(fee.txFeeFixedUsdt)} />
                                                        </div>
                                                    </div>

                                                    <div className="border-t border-slate-100 pt-4">
                                                        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Limit</h4>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <ValueRow label="Rechaj minimòm" value={formatUSD(fee.minRechargeUsdt)} />
                                                            <ValueRow label="Rechaj maksimòm" value={formatUSD(fee.maxRechargeUsdt)} />
                                                            <ValueRow label="Limit depans jounalye" value={formatUSD(fee.dailySpendLimitUsdt)} />
                                                            <ValueRow label="Limit depans mansyèl" value={formatUSD(fee.monthlySpendLimitUsdt)} />
                                                        </div>
                                                    </div>

                                                    <div className="border-t border-slate-100 pt-4">
                                                        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Referans & Estati</h4>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <ValueRow label="Komisyon referans" value={formatPercent(fee.referralCommissionPercent)} />
                                                            <div>
                                                                <p className="text-xs text-slate-400">Estati konfigirasyon</p>
                                                                <Badge className={cn(
                                                                    'mt-1 font-semibold',
                                                                    fee.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                                )}>
                                                                    {fee.isActive ? 'AKTIF' : 'INAKTIF'}
                                                                </Badge>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ─── TAB CONTENT: Tiers ─────────────────────────────────────────────────── */}
                    {activeTab === 'tiers' && (
                        <div className="space-y-6">
                            {loadingTiers ? (
                                <div className="py-24 text-center">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto" />
                                    <p className="text-slate-500 mt-3 text-sm">Ap chaje nivo kat yo...</p>
                                </div>
                            ) : tiers.length === 0 ? (
                                <Card className="py-16 text-center text-slate-400 font-medium">
                                    <Layers className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                                    Okenn nivo kat jwenn
                                </Card>
                            ) : (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {tiers.map((tier) => (
                                        <Card key={tier.id} className="border-slate-200 shadow-sm overflow-hidden bg-white">
                                            <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between py-4">
                                                <div>
                                                    <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                                        <span
                                                            className="w-3 h-3 rounded-full shadow-sm"
                                                            style={{ backgroundColor: tier.badgeColor || '#ccc' }}
                                                        />
                                                        {tier.name}
                                                    </CardTitle>
                                                    <CardDescription className="text-xs">
                                                        Slug: <code className="bg-slate-100 px-1 py-0.5 rounded text-xs font-bold">{tier.slug}</code>
                                                    </CardDescription>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    className="border-slate-200 text-primary-600 gap-1.5 h-9"
                                                    onClick={() => {
                                                        setSelectedTier(tier);
                                                        setTempTierFields(tier);
                                                    }}
                                                >
                                                    <Edit2 className="h-3.5 w-3.5" /> Modifye
                                                </Button>
                                            </CardHeader>
                                            <CardContent className="pt-6 space-y-4">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <ValueRow label="HyperPay Card Type ID" value={tier.hyperpayCardTypeId} />
                                                    <ValueRow label="Limit Kat pou chak Kliyan" value={String(tier.maxCardsPerClient)} />
                                                    <ValueRow label="Badge Koulè" value={tier.badgeColor} />
                                                    <ValueRow label="Lòd Klasman" value={String(tier.sortOrder)} />
                                                    <ValueRow label="Deskripsyon" value={tier.description || '—'} colSpan={2} />
                                                    
                                                    <div className="col-span-2">
                                                        <p className="text-xs text-slate-400">Estati</p>
                                                        <Badge className={cn(
                                                            'mt-1 font-semibold',
                                                            tier.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                        )}>
                                                            {tier.isActive ? 'AKTIF' : 'INAKTIF'}
                                                        </Badge>
                                                    </div>
                                                </div>

                                                <div className="border-t border-slate-100 pt-4">
                                                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Avantaj & Karakteristik</p>
                                                    {Array.isArray(tier.features) && tier.features.length > 0 ? (
                                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                                            {tier.features.map((feat, idx) => (
                                                                <Badge key={idx} variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-xs">
                                                                    ✓ {feat}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm text-slate-400 italic">Okenn avantaj endike</span>
                                                    )}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ─── TAB CONTENT: Kont Marchan ──────────────────────────────────────────── */}
                    {activeTab === 'account' && (
                        <div className="space-y-6">
                            {loadingAccount ? (
                                <div className="py-24 text-center">
                                    <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto" />
                                    <p className="text-slate-500 mt-3 text-sm">Ap chaje enfòmasyon sou kont lan...</p>
                                </div>
                            ) : !account ? (
                                <Card className="py-16 text-center text-slate-400 font-medium bg-white">
                                    <Wallet className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                                    Pa gen okenn kont marchan ki asosye
                                </Card>
                            ) : (
                                <div className="space-y-6 max-w-4xl mx-auto">
                                    {/* Stats grid */}
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                                        <Card className="border-none shadow-md bg-gradient-to-br from-primary-600 to-primary-700 text-white relative overflow-hidden">
                                            <CardContent className="pt-6 pb-6">
                                                <p className="text-primary-100 text-xs font-semibold uppercase tracking-wider">Solde USDT</p>
                                                <p className="text-3xl sm:text-4xl font-extrabold mt-2 font-mono">
                                                    {account.balanceUsdt != null ? account.balanceUsdt.toLocaleString('en-US', { minimumFractionDigits: 2 }) : '0.00'}
                                                </p>
                                                <div className="absolute right-4 bottom-4 opacity-10">
                                                    <Wallet className="h-24 w-24" />
                                                </div>
                                            </CardContent>
                                        </Card>

                                        <Card className="border-none shadow-md bg-gradient-to-br from-indigo-500 to-indigo-600 text-white relative overflow-hidden">
                                            <CardContent className="pt-6 pb-6">
                                                <p className="text-indigo-100 text-xs font-semibold uppercase tracking-wider">Solde USD Equivalent</p>
                                                <p className="text-3xl sm:text-4xl font-extrabold mt-2 font-mono">
                                                    {formatUSD(account.balanceUsd)}
                                                </p>
                                                <div className="absolute right-4 bottom-4 opacity-10">
                                                    <DollarSign className="h-24 w-24" />
                                                </div>
                                            </CardContent>
                                        </Card>

                                        <Card className="border-none shadow-md bg-gradient-to-br from-emerald-500 to-emerald-600 text-white relative overflow-hidden">
                                            <CardContent className="pt-6 pb-6">
                                                <p className="text-emerald-100 text-xs font-semibold uppercase tracking-wider">Total Frè Kolekte</p>
                                                <p className="text-3xl sm:text-4xl font-extrabold mt-2 font-mono">
                                                    {account.totalCollectedFeesUsdt != null ? account.totalCollectedFeesUsdt.toLocaleString('en-US', { minimumFractionDigits: 2 }) : '0.00'} <span className="text-sm font-normal text-emerald-100">USDT</span>
                                                </p>
                                                <div className="absolute right-4 bottom-4 opacity-10">
                                                    <Info className="h-24 w-24" />
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </div>

                                    {/* Account details card */}
                                    <Card className="border-slate-200 shadow-sm bg-white overflow-hidden">
                                        <CardHeader className="border-b border-slate-100 py-4 bg-slate-50/50">
                                            <CardTitle className="text-base font-bold text-slate-800 flex items-center gap-2">
                                                <ShieldCheck className="h-5 w-5 text-emerald-600" /> Detay Kont Marchan
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent className="pt-6">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                                <ValueRow label="Non Kont" value={account.name} />
                                                <ValueRow label="Dènye Rekonsilyasyon" value={formatDate(account.lastReconciledAt)} />
                                                <ValueRow label="Dènye Mizajou" value={formatDate(account.updatedAt)} />
                                            </div>

                                            <div className="mt-8 flex items-start gap-3 p-4 rounded-xl bg-blue-50/60 border border-blue-100/50 text-sm text-slate-700">
                                                <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <p className="font-semibold text-blue-900">Nòt sou kont lan:</p>
                                                    <p className="mt-1 text-slate-600 leading-relaxed">
                                                        Solde sa reflete kont operasyon Bousanm nan HyperPay. Pou rechaje oswa fè vire, kontakte depatman finans la dirèkteman. Depo ak retrè yo dwe anrejistre pou kontablite.
                                                    </p>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>

            {/* ─── DIALOG: Edit System Config ─────────────────────────────────────────── */}
            <Dialog
                open={!!selectedConfig}
                onOpenChange={(open) => { if (!open) setSelectedConfig(null); }}
            >
                <DialogContent className="sm:max-w-lg">
                    {selectedConfig && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2 text-lg font-bold text-slate-900">
                                    <Settings className="h-5 w-5 text-primary-500" />
                                    Edite Konfigirasyon
                                </DialogTitle>
                                <DialogDescription className="font-mono text-xs font-semibold text-primary-600 bg-slate-50 px-2 py-1 rounded w-fit mt-1">
                                    {selectedConfig.key}
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-4 py-4">
                                {selectedConfig.description && (
                                    <div className="text-sm text-slate-500 bg-slate-50 p-3 rounded-lg border border-slate-100 flex items-start gap-2">
                                        <Info className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                        <span>{selectedConfig.description}</span>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-slate-700">
                                        Valè konfigirasyon
                                    </label>
                                    
                                    {selectedConfig.key === 'card_apply_mode' ? (
                                        <select
                                            value={tempConfigValue}
                                            onChange={(e) => setTempConfigValue(e.target.value)}
                                            className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                                        >
                                            <option value="instant">instant — Apwobasyon otomatik</option>
                                            <option value="manual_review">manual_review — Revizyon manyèl (default)</option>
                                            <option value="disabled">disabled — Dezaktive tout demand</option>
                                        </select>
                                    ) : selectedConfig.valueType === 'boolean' ? (
                                        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                                            <input
                                                type="checkbox"
                                                id="boolean-config-edit"
                                                className="h-5 w-5 rounded border-slate-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                                                checked={tempConfigValue === 'true'}
                                                onChange={(e) => setTempConfigValue(e.target.checked ? 'true' : 'false')}
                                            />
                                            <label htmlFor="boolean-config-edit" className="text-sm font-semibold text-slate-700 cursor-pointer select-none">
                                                Aktive konfigirasyon sa a
                                            </label>
                                        </div>
                                    ) : selectedConfig.valueType === 'number' ? (
                                        <Input
                                            type="number"
                                            step={selectedConfig.key.includes('fx_rate_usd') ? '0.0001' : '0.01'}
                                            min="0"
                                            className="border-slate-200 rounded-lg focus:ring-primary-500"
                                            value={tempConfigValue}
                                            onChange={(e) => setTempConfigValue(e.target.value)}
                                        />
                                    ) : (
                                        <Input
                                            type="text"
                                            className="border-slate-200 rounded-lg focus:ring-primary-500"
                                            value={tempConfigValue}
                                            onChange={(e) => setTempConfigValue(e.target.value)}
                                        />
                                    )}
                                </div>
                            </div>

                            <DialogFooter className="gap-2 sm:gap-0 border-t border-slate-100 pt-4">
                                <Button
                                    variant="outline"
                                    className="border-slate-200"
                                    onClick={() => setSelectedConfig(null)}
                                >
                                    Anile
                                </Button>
                                <Button
                                    className="bg-primary-600 hover:bg-primary-700 text-white gap-2 shadow-md"
                                    onClick={handleUpdateSystemConfig}
                                    disabled={savingConfig}
                                >
                                    {savingConfig ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                    Konfime
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* ─── DIALOG: Edit Fee Config ────────────────────────────────────────────── */}
            <Dialog
                open={!!selectedFee}
                onOpenChange={(open) => { if (!open) setSelectedFee(null); }}
            >
                <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
                    {selectedFee && (
                        <>
                            <DialogHeader className="border-b border-slate-100 pb-3">
                                <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                    <DollarSign className="h-5 w-5 text-primary-500" />
                                    Modifye Frè & Limit ({selectedFee.cardTier})
                                </DialogTitle>
                                <DialogDescription>
                                    Konfigirasyon tarif ak nivo depo/depans yo
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-6 py-4">
                                {/* Frè Aplikasyon */}
                                <div className="space-y-4">
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Frè Aplikasyon & Mansyèl</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-slate-600">Frè Aplikasyon Total (USD)</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.applyFeeUsdt || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, applyFeeUsdt: e.target.value as any })}
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-slate-600">Frè Mansyèl (USD)</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.monthlyFeeUsdt || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, monthlyFeeUsdt: e.target.value as any })}
                                            />
                                        </div>
                                        <div className="space-y-1.5 pl-3 border-l-2 border-slate-100">
                                            <label className="text-xs font-semibold text-slate-600">└→ Pati HyperPay (USD)</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.hyperpayApplyFeeUsdt || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, hyperpayApplyFeeUsdt: e.target.value as any })}
                                            />
                                        </div>
                                        <div className="space-y-1.5 pl-3 border-l-2 border-slate-100">
                                            <label className="text-xs font-semibold text-slate-600">└→ Pati Bousanm (USD)</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.bousanmApplyFeeUsdt || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, bousanmApplyFeeUsdt: e.target.value as any })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Frè Rechaj */}
                                <div className="space-y-4 border-t border-slate-100 pt-4">
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Frè Rechaj</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1.5 col-span-2">
                                            <label className="text-xs font-semibold text-slate-600">Frè rechaj TOTAL %</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.rechargeFeePercent || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, rechargeFeePercent: e.target.value as any })}
                                            />
                                            <p className="text-[10px] text-slate-500 italic mt-1 bg-slate-50 px-2 py-1 rounded">
                                                ⚠ Frè total = HyperPay % + Bousanm %. Si ou chanje valè yo separe, mete rechargeFeePercent egal sòm yo.
                                            </p>
                                        </div>
                                        <div className="space-y-1.5 pl-3 border-l-2 border-slate-100">
                                            <label className="text-xs font-semibold text-slate-600">└→ Pati HyperPay %</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.hyperPayFeePercent || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, hyperPayFeePercent: e.target.value as any })}
                                            />
                                        </div>
                                        <div className="space-y-1.5 pl-3 border-l-2 border-slate-100">
                                            <label className="text-xs font-semibold text-slate-600">└→ Pati Bousanm %</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.bousanmFeePercent || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, bousanmFeePercent: e.target.value as any })}
                                            />
                                        </div>
                                        <div className="space-y-1.5 col-span-2">
                                            <label className="text-xs font-semibold text-slate-600">Frè rechaj fiks (USD)</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.rechargeFeeFixedUsdt || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, rechargeFeeFixedUsdt: e.target.value as any })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Frè Transaksyon */}
                                <div className="space-y-4 border-t border-slate-100 pt-4">
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Frè Transaksyon</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-slate-600">Frè transaksyon %</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.txFeePercent || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, txFeePercent: e.target.value as any })}
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-slate-600">Frè transaksyon fiks (USD)</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.txFeeFixedUsdt || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, txFeeFixedUsdt: e.target.value as any })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Limit yo */}
                                <div className="space-y-4 border-t border-slate-100 pt-4">
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Limit Depans & Depo</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-slate-600">Rechaj minimòm (USD)</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.minRechargeUsdt || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, minRechargeUsdt: e.target.value as any })}
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-slate-600">Rechaj maksimòm (USD)</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.maxRechargeUsdt || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, maxRechargeUsdt: e.target.value as any })}
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-slate-600">Limit depans jounalye (USD)</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.dailySpendLimitUsdt || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, dailySpendLimitUsdt: e.target.value as any })}
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-slate-600">Limit depans mansyèl (USD)</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.monthlySpendLimitUsdt || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, monthlySpendLimitUsdt: e.target.value as any })}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Referans & Estati */}
                                <div className="space-y-4 border-t border-slate-100 pt-4">
                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Referans & Estati</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-slate-600">Komisyon Referans %</label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={tempFeeFields.referralCommissionPercent || 0}
                                                onChange={(e) => setTempFeeFields({ ...tempFeeFields, referralCommissionPercent: e.target.value as any })}
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-semibold text-slate-600 block mb-1">Estati Aktif</label>
                                            <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-md border h-10">
                                                <input
                                                    type="checkbox"
                                                    id="fee-config-active-check"
                                                    className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                                                    checked={tempFeeFields.isActive || false}
                                                    onChange={(e) => setTempFeeFields({ ...tempFeeFields, isActive: e.target.checked })}
                                                />
                                                <label htmlFor="fee-config-active-check" className="text-xs font-semibold text-slate-700 cursor-pointer">
                                                    Aktive konfigirasyon sa
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <DialogFooter className="gap-2 sm:gap-0 border-t border-slate-100 pt-4">
                                <Button
                                    variant="outline"
                                    className="border-slate-200"
                                    onClick={() => setSelectedFee(null)}
                                >
                                    Anile
                                </Button>
                                <Button
                                    className="bg-primary-600 hover:bg-primary-700 text-white gap-2 shadow-md"
                                    onClick={handleUpdateFeeConfig}
                                    disabled={savingFee}
                                >
                                    {savingFee ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                    Konfime
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* ─── DIALOG: Edit Tier Config ───────────────────────────────────────────── */}
            <Dialog
                open={!!selectedTier}
                onOpenChange={(open) => { if (!open) setSelectedTier(null); }}
            >
                <DialogContent className="sm:max-w-lg">
                    {selectedTier && (
                        <>
                            <DialogHeader className="border-b border-slate-100 pb-3">
                                <DialogTitle className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                    <Layers className="h-5 w-5 text-primary-500" />
                                    Modifye Nivo Kat ({selectedTier.name})
                                </DialogTitle>
                                <DialogDescription>
                                    Ajiste detay ak règleman jeneral nivo kat la
                                </DialogDescription>
                            </DialogHeader>

                            <div className="space-y-4 py-4">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-slate-600">Non Nivo</label>
                                    <Input
                                        type="text"
                                        value={tempTierFields.name || ''}
                                        onChange={(e) => setTempTierFields({ ...tempTierFields, name: e.target.value })}
                                    />
                                </div>

                                <div className="space-y-1.5">
                                    <label className="text-xs font-semibold text-slate-600">Deskripsyon</label>
                                    <Textarea
                                        className="resize-none min-h-[60px]"
                                        value={tempTierFields.description || ''}
                                        onChange={(e) => setTempTierFields({ ...tempTierFields, description: e.target.value })}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-semibold text-slate-600">Badge Koulè (Hex/Tailwind)</label>
                                        <Input
                                            type="text"
                                            value={tempTierFields.badgeColor || ''}
                                            onChange={(e) => setTempTierFields({ ...tempTierFields, badgeColor: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-semibold text-slate-600">Limit Kat pa Kliyan</label>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={tempTierFields.maxCardsPerClient || 1}
                                            onChange={(e) => setTempTierFields({ ...tempTierFields, maxCardsPerClient: e.target.value as any })}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-semibold text-slate-600">Lòd Klasman (Sort Order)</label>
                                        <Input
                                            type="number"
                                            value={tempTierFields.sortOrder || 0}
                                            onChange={(e) => setTempTierFields({ ...tempTierFields, sortOrder: e.target.value as any })}
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-xs font-semibold text-slate-600 block mb-1">Estati Aktif</label>
                                        <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-md border h-10">
                                            <input
                                                type="checkbox"
                                                id="tier-config-active-check"
                                                className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                                                checked={tempTierFields.isActive || false}
                                                onChange={(e) => setTempTierFields({ ...tempTierFields, isActive: e.target.checked })}
                                            />
                                            <label htmlFor="tier-config-active-check" className="text-xs font-semibold text-slate-700 cursor-pointer">
                                                Aktive nivo sa a
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <DialogFooter className="gap-2 sm:gap-0 border-t border-slate-100 pt-4">
                                <Button
                                    variant="outline"
                                    className="border-slate-200"
                                    onClick={() => setSelectedTier(null)}
                                >
                                    Anile
                                </Button>
                                <Button
                                    className="bg-primary-600 hover:bg-primary-700 text-white gap-2 shadow-md"
                                    onClick={handleUpdateCardTier}
                                    disabled={savingTier}
                                >
                                    {savingTier ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                                    Konfime
                                </Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ─── Helper Component: Value Row ─────────────────────────────────────────────
function ValueRow({
    label,
    value,
    indent,
    highlight,
    colSpan,
}: {
    label: string;
    value?: string | null;
    indent?: boolean;
    highlight?: boolean;
    colSpan?: number;
}) {
    return (
        <div className={cn(colSpan ? `col-span-${colSpan}` : '', indent ? 'pl-4 border-l-2 border-slate-100' : '')}>
            <p className="text-xs text-slate-400">{label}</p>
            <p className={cn(
                'text-sm font-semibold mt-0.5',
                highlight ? 'text-primary-600 font-bold text-base' : 'text-slate-800'
            )}>
                {value ?? '—'}
            </p>
        </div>
    );
}
